import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Trophy,
  Medal,
  Target,
  Flame,
  ChevronDown,
  Crown
} from "lucide-react";

export default function TeacherLeaderboard() {
  const { teacher } = useOutletContext();
  const [leaderboard, setLeaderboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState("");

  const assignedGrades = teacher?.assignments 
    ? Object.keys(teacher.assignments).filter(g => teacher.assignments[g]?.length > 0).sort((a, b) => parseInt(a) - parseInt(b))
    : [];

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedGrade]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      let url = `${API}/teacher/dashboard/leaderboard?limit=10`;
      if (selectedGrade) {
        url += `&grade=${selectedGrade}`;
      }
      
      const response = await fetch(url, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.leaderboard_by_grade || {});
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-500" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-400" />;
      case 3:
        return <Medal className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-gray-400">{rank}</span>;
    }
  };

  const getRankBadgeColor = (rank) => {
    switch (rank) {
      case 1:
        return "bg-gradient-to-r from-yellow-400 to-amber-500 text-white";
      case 2:
        return "bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800";
      case 3:
        return "bg-gradient-to-r from-amber-500 to-amber-600 text-white";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const gradeKeys = Object.keys(leaderboard).sort((a, b) => {
    const gradeA = parseInt(a.replace("Grade ", ""));
    const gradeB = parseInt(b.replace("Grade ", ""));
    return gradeA - gradeB;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Trophy className="w-7 h-7 text-amber-500" />
            Class Leaderboard
          </h1>
          <p className="text-gray-500 mt-1">
            Top performers in your classes
          </p>
        </div>

        {/* Grade Filter */}
        <div className="relative">
          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            className="appearance-none w-full md:w-48 px-4 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Grades</option>
            {assignedGrades.map((grade) => (
              <option key={grade} value={grade}>Grade {grade}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Leaderboard */}
      {loading ? (
        <div className="space-y-6">
          {[1, 2].map((i) => (
            <div key={i} className="h-64 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
      ) : gradeKeys.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {gradeKeys.map((gradeKey) => {
            const students = leaderboard[gradeKey] || [];
            
            return (
              <Card key={gradeKey} className="border-0 shadow-md overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-4">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Trophy className="w-5 h-5" />
                      {gradeKey}
                    </span>
                    <Badge className="bg-white/20 text-white">
                      Top {students.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {students.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {students.map((student, index) => (
                        <div 
                          key={index}
                          className={`flex items-center justify-between p-4 ${
                            student.rank <= 3 ? 'bg-gradient-to-r from-yellow-50/50 to-transparent' : ''
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            {/* Rank Badge */}
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getRankBadgeColor(student.rank)}`}>
                              {getRankIcon(student.rank)}
                            </div>
                            
                            {/* Student Info */}
                            <div>
                              <p className="font-medium text-gray-900">{student.name}</p>
                              <div className="flex items-center gap-3 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Target className="w-3.5 h-3.5" />
                                  {student.total_questions} Q
                                </span>
                                {student.streak > 0 && (
                                  <span className="flex items-center gap-1 text-orange-500">
                                    <Flame className="w-3.5 h-3.5" />
                                    {student.streak}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Accuracy */}
                          <div className="text-right">
                            <p className={`text-lg font-bold ${
                              student.accuracy >= 80 ? 'text-emerald-600' :
                              student.accuracy >= 60 ? 'text-amber-600' :
                              'text-red-600'
                            }`}>
                              {student.accuracy}%
                            </p>
                            <p className="text-xs text-gray-500">accuracy</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-12 text-center text-gray-500">
                      <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No data yet</p>
                      <p className="text-sm">Students will appear here once they start learning</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-md">
          <CardContent className="py-16 text-center">
            <Trophy className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Leaderboard Data</h3>
            <p className="text-gray-500">
              Leaderboard will populate as students complete questions.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-blue-50">
        <CardContent className="p-4">
          <p className="text-sm text-blue-700">
            <strong>How rankings work:</strong> Students are ranked by accuracy percentage. 
            Students must answer at least 1 question to appear on the leaderboard.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
